import { getRegionalUpdaterGate, type RegionalUpdaterGate } from '@/features/flags';
import { getCachedKindeRegion } from '@/hooks/query/useQueryKindeProperties';
import { type Region } from '@heidi/schema/ml-scribe';
import { check } from '@tauri-apps/plugin-updater';
import { UpdaterBridgeError, updaterBridge } from './index';

/**
 * The resolved updater feed for the current gate + region. This is the single
 * source of truth for the compliance-critical routing decision (APP-6113 §5.19,
 * DC3) — every consumer (the optional-updater model, the settings debug page,
 * the popover trigger) routes through {@link selectUpdaterFeed} so the
 * fail-closed ladder can't drift between copies.
 *
 * - `blocked`  — fail-closed: the flag provider hasn't resolved, or the gate is
 *   ON but the authenticated region is unresolved. No update is offered; never
 *   fall through to a default/`stable` channel (a single global feed could serve
 *   UK/EU an uncertified version).
 * - `global`   — gate OFF: today's global updater (no region isolation).
 * - `regional` — gate ON + region resolved: bind the CrabNebula channel to the
 *   region in Rust.
 */
export type UpdaterFeed =
  | { kind: 'blocked' }
  | { kind: 'global' }
  | { kind: 'regional'; region: Region };

/**
 * The single fail-closed decision. Pure — takes the gate and region as inputs so
 * both the non-React path ({@link resolveUpdaterFeed}) and React consumers
 * (settings / popover, which read them via hooks) share the exact same logic.
 *
 * `region` MUST come from `kindeProperties.region` — never `getRegion()` /
 * `useRegion()`, which fail open to AU and honour `?region=` (C10).
 */
export function selectUpdaterFeed(
  gate: RegionalUpdaterGate,
  region: Region | undefined
): UpdaterFeed {
  if (gate === 'unknown') return { kind: 'blocked' }; // DC3: provider not ready
  if (gate === 'off') return { kind: 'global' };
  if (!region) return { kind: 'blocked' }; // DC3: ON but region unresolved
  return { kind: 'regional', region };
}

/**
 * Non-React feed resolution (used by the rematch model). Reads the static gate
 * and the cached region. Returns `{ kind: 'blocked' }` until both resolve.
 */
export function resolveUpdaterFeed(): UpdaterFeed {
  return selectUpdaterFeed(getRegionalUpdaterGate(), getCachedKindeRegion());
}

/**
 * The global feed calls the JS plugin directly, so its errors never pass
 * through Rust's `plugin_error_code` and arrive as the plugin's own message.
 * These map that text to the same codes. Verified against tauri-plugin-updater
 * 2.13.3 (and its minisign-verify, base64 and reqwest errors); the fixed-text
 * cases are pinned to the Rust classifier by `updater_plugin_error_messages.json`.
 */
const GLOBAL_ERROR_PATTERNS: ReadonlyArray<[RegExp, string]> = [
  [/was signed for version .* announced version/, 'UPDATER_SIGNED_VERSION_MISMATCH'],
  [/does not specify the version it was signed for/, 'UPDATER_SIGNED_VERSION_MISSING'],
  [
    /signature verification failed|created with a different key|Invalid encoding in minisign data|Unexpected signature algorithm|signature algorithm is not supported|StreamVerifier only supports|could not be decoded, please check if it is a valid base64|^Invalid (symbol|input length|last symbol|padding)/,
    'UPDATER_SIGNATURE_INVALID',
  ],
  [
    /failed to install the update and to restore the previous app/,
    'UPDATER_PREVIOUS_APP_NOT_RESTORED',
  ],
  [
    /error sending request|request or response body error|error decoding response body|error following redirect|error upgrading connection|HTTP status (client|server) error|operation timed out|Download request failed with status/,
    'UPDATER_NETWORK',
  ],
  [/in the response `platforms` object/, 'UPDATER_TARGET_NOT_FOUND'],
  [/Could not fetch a valid release JSON from the remote/, 'UPDATER_RELEASE_NOT_FOUND'],
  [/^Unsupported (application architecture|OS)/, 'UPDATER_UNSUPPORTED_PLATFORM'],
  [/Authentication failed or was cancelled/, 'UPDATER_INSTALL_PERMISSION_DENIED'],
  [
    /Failed to determine updater package extract path|temp directory is not on the same mount point|failed to create temporary directory|binary for the current target not found in the archive|invalid updater binary format|^Failed to install (\.deb )?package/,
    'UPDATER_INSTALL_IO',
  ],
  [/^Failed to format date$|at line \d+ column \d+/, 'UPDATER_MANIFEST_INVALID'],
  [/Updater does not have any endpoints set/, 'UPDATER_NO_ENDPOINTS'],
  [/must use a secure protocol/, 'UPDATER_INSECURE_ENDPOINT'],
];

/**
 * Text the patterns don't recognise (an OS I/O error, a new plugin message) is
 * coded `UPDATER_UNCLASSIFIED` rather than left blank, so Datadog can count it.
 */
export function classifyGlobalUpdaterError(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  return (
    GLOBAL_ERROR_PATTERNS.find(([pattern]) => pattern.test(message))?.[1] ??
    'UPDATER_UNCLASSIFIED'
  );
}

/** Rethrow a global-feed plugin error as a coded bridge error. */
async function withGlobalErrorCode<T>(run: () => Promise<T>): Promise<T> {
  try {
    return await run();
  } catch (error) {
    if (error instanceof UpdaterBridgeError) throw error;
    const message = error instanceof Error ? error.message : String(error);
    throw new UpdaterBridgeError(message, classifyGlobalUpdaterError(error));
  }
}

/**
 * Execute a check for the resolved feed. `blocked` resolves to `null` (no
 * update) defensively so a mis-routed caller still fails closed. Both feeds
 * normalise to `{ version, body }` so downstream wiring is identical.
 *
 * Deliberately imposes no deadline of its own. Both updater calls are bounded
 * inside the Rust updater, which is the layer that owns the connection and the
 * bundle: a timeout there genuinely cancels the transfer, whereas one here
 * could only stop us waiting while the download continued and still replaced
 * the app. A frontend deadline would also be a new failure branch reaching
 * every desktop user on the global path, tunable only by shipping a build.
 */
export async function checkUpdateForFeed(
  feed: UpdaterFeed
): Promise<{ version: string; body?: string } | null> {
  if (feed.kind === 'blocked') return null;
  if (feed.kind === 'regional') {
    return updaterBridge.checkUpdateForRegion(feed.region);
  }
  const update = await withGlobalErrorCode(() => check()); // OFF: global updater
  return update ? { version: update.version, body: update.body } : null;
}

/**
 * Install the update for the resolved feed. Resolves `true` when an update was
 * installed (the caller relaunches). `blocked` resolves to `false`.
 */
export async function installUpdateForFeed(feed: UpdaterFeed): Promise<boolean> {
  if (feed.kind === 'blocked') return false;
  if (feed.kind === 'regional') {
    return updaterBridge.installUpdateForRegion(feed.region);
  }
  return withGlobalErrorCode(async () => {
    const update = await check(); // OFF: global updater
    if (!update) return false;
    await update.downloadAndInstall();
    return true;
  });
}
